/**
 * Who's going → "Whose taste counts most?" (WhosGoingModal, Home search).
 *
 * The modal offers three choices instead of per-person sliders; the search
 * ranking still takes per-person weights (`groupWeights`, summing to 1), so
 * these two functions translate between them.
 *
 *   equal — everyone counts the same. Returns null: the server's default
 *           is an equal share, so no weights need to travel.
 *   kids  — each child counts double an adult. Only offered when the group
 *           has both children and adults.
 *   lean  — one person counts double everyone else.
 *
 * Weights that match none of the three read back as 'equal'. Nothing makes
 * such weights any more (the results page's sliders are gone), but a stale
 * sessionStorage value can still arrive.
 *
 * Two choices can give the same weights: with exactly one child, "Kids first"
 * and "One person… → that child" both count the child double. `preferred` (the
 * choice the user last saved) breaks the tie, so the modal reopens on what was
 * picked. It is used only when it reproduces the weights exactly.
 */

const isChild = (p) => p?.type === 'child';

export function canPutKidsFirst(people) {
  return people.some(isChild) && people.some(p => !isChild(p));
}

export function weightsForTaste(people, taste) {
  if (!Array.isArray(people) || people.length < 2 || !taste) return null;
  let units;
  if (taste.mode === 'kids' && canPutKidsFirst(people)) {
    units = people.map(p => (isChild(p) ? 2 : 1));
  } else if (taste.mode === 'lean' && people.some(p => p.id === taste.leanId)) {
    units = people.map(p => (p.id === taste.leanId ? 2 : 1));
  } else {
    return null;
  }
  const total = units.reduce((a, b) => a + b, 0);
  const weights = {};
  people.forEach((p, i) => { weights[p.id] = units[i] / total; });
  return weights;
}

const close = (a, b) => Math.abs(a - b) < 0.01;

function sameWeights(people, a, b) {
  return people.every(p => close(a[p.id] ?? 0, b[p.id] ?? 0));
}

export function tasteFromWeights(people, weights, preferred = null) {
  if (!weights || Object.keys(weights).length === 0 || !Array.isArray(people) || people.length < 2) {
    return { mode: 'equal', leanId: null };
  }
  const equal = 1 / people.length;
  if (people.every(p => close(weights[p.id] ?? 0, equal))) return { mode: 'equal', leanId: null };
  if (preferred) {
    const mine = weightsForTaste(people, preferred);
    if (mine && sameWeights(people, weights, mine)) {
      return { mode: preferred.mode, leanId: preferred.mode === 'lean' ? preferred.leanId : null };
    }
  }
  const kids = weightsForTaste(people, { mode: 'kids' });
  if (kids && sameWeights(people, weights, kids)) return { mode: 'kids', leanId: null };
  for (const p of people) {
    const lean = weightsForTaste(people, { mode: 'lean', leanId: p.id });
    if (lean && sameWeights(people, weights, lean)) return { mode: 'lean', leanId: p.id };
  }
  return { mode: 'equal', leanId: null };
}
